import { z } from 'zod';
import { uuid, isoDateTime } from './common.js';
export const orderType = z.enum(['regular', 'gift', 'points']);
/** awaiting_approval and awaiting_payment are storefront orders that are not yet for the warehouse. */
export const orderStatus = z.enum(['new', 'released', 'in_progress', 'partially_shipped', 'shipped', 'completed', 'on_hold', 'needs_check', 'cancelled', 'awaiting_approval', 'awaiting_payment']);
export const invoiceStatus = z.enum(['not_due', 'due', 'invoiced', 'failed', 'none']);
export const deliveryMethod = z.enum(['pickup', 'parcel_shop', 'business', 'home']);
export const paymentProvider = z.enum(['bambora', 'dibs', 'invoice', 'gift', 'points', 'unknown']);
export const addressSnapshot = z.object({
    name: z.string().nullable(),
    company: z.string().nullable(),
    street1: z.string().nullable(),
    street2: z.string().nullable(),
    zip: z.string().nullable(),
    city: z.string().nullable(),
    country: z.string().nullable(),
    countryCode: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    department: z.string().nullable(),
    customFields: z.array(z.string()).default([]),
});
export const orderLineDto = z.object({
    id: uuid,
    externalLineId: z.string().nullable(),
    variantId: uuid.nullable(),
    sku: z.string(),
    title: z.string(),
    variantTitle: z.string().nullable(),
    quantity: z.number().int(),
    unitPriceMinor: z.number().int(),
    /** The line total as 3x states it; the unit price is derived from it. */
    lineTotalMinor: z.number().int(),
    imageUrl: z.string().nullable(),
    fulfilledQty: z.number().int(),
});
/** What Navision holds for a 3x order number: the sales order, its posted shipment and invoice. */
export const navisionOrderState = z.enum(['open', 'released', 'shipped', 'invoiced', 'archived']);
export const navisionOrderDto = z.object({
    state: navisionOrderState,
    salesOrderNo: z.string().nullable(),
    shopId: z.string().nullable(),
    orderDate: z.string().nullable(),
    shipmentDate: z.string().nullable(),
    locationCode: z.string().nullable(),
    qtyOrdered: z.number(),
    qtyShipped: z.number(),
    qtyInvoiced: z.number(),
    shipmentNo: z.string().nullable(),
    shipmentPostedAt: z.string().nullable(),
    trackingNo: z.string().nullable(),
    invoiceNo: z.string().nullable(),
    invoicePostedAt: z.string().nullable(),
    invoiceAmountMinor: z.number().int().nullable(),
    customerNo: z.string().nullable(),
    fetchedAt: isoDateTime,
});
export const orderListItemDto = z.object({
    id: uuid,
    number: z.number().int(),
    externalNumber: z.string().nullable(),
    shopId: uuid.nullable(),
    shopName: z.string(),
    customerName: z.string().nullable(),
    type: orderType,
    status: orderStatus,
    invoiceStatus: invoiceStatus,
    paymentProvider: paymentProvider,
    deliveryMethod: deliveryMethod,
    totalMinor: z.number().int(),
    currency: z.string().nullable(),
    lineCount: z.number().int(),
    needsCheckReason: z.string().nullable(),
    placedAt: isoDateTime.nullable(),
    importedAt: isoDateTime,
    /** Navision's view of the order, null when Navision has no sales document for it. */
    navision: navisionOrderDto.nullable(),
    /** Placed in a demo shop (docs/design/12 section 6). */
    demo: z.boolean(),
});
export const orderDto = orderListItemDto.extend({
    source: z.string(),
    externalId: z.string().nullable(),
    customerEmail: z.string().nullable(),
    customerPhone: z.string().nullable(),
    billingAddress: addressSnapshot.nullable(),
    shippingAddress: addressSnapshot.nullable(),
    subtotalMinor: z.number().int(),
    shippingMinor: z.number().int(),
    discountMinor: z.number().int(),
    totalWeightG: z.number().int().nullable(),
    payment: z.object({
        provider: paymentProvider,
        method: z.string().nullable(),
        merchantId: z.string().nullable(),
        transactionId: z.string().nullable(),
        financialStatus: z.string().nullable(),
    }),
    delivery: z.object({
        method: deliveryMethod,
        provider: z.string().nullable(),
        parcelShopId: z.string().nullable(),
        instructionsFlag: z.boolean(),
        deliveryDate: z.string().nullable(),
        deliveryWeek: z.string().nullable(),
    }),
    note: z.string().nullable(),
    internalNote: z.string().nullable(),
    /** Why the last automatic release attempt did nothing (continuous shops), null when released or never tried. */
    autoReleaseBlock: z.string().nullable(),
    autoReleaseAttemptAt: isoDateTime.nullable(),
    externalFulfilmentStatus: z.string().nullable(),
    externalFinancialStatus: z.string().nullable(),
    lines: z.array(orderLineDto),
    events: z.array(z.object({
        id: uuid,
        at: isoDateTime,
        actorName: z.string(),
        fromStatus: z.string().nullable(),
        toStatus: z.string().nullable(),
        note: z.string().nullable(),
    })),
});
export const orderListQuery = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(200).default(50),
    q: z.string().trim().max(200).optional(),
    status: orderStatus.optional(),
    shopId: uuid.optional(),
    type: orderType.optional(),
    deliveryMethod: deliveryMethod.optional(),
    needsCheck: z.coerce.boolean().optional(),
    /** `handled`: shipped or invoiced in Navision; `open`: a sales order exists but is not shipped; `none`: Navision knows nothing. */
    navision: z.enum(['handled', 'open', 'none']).optional(),
    from: z.string().optional(),
    to: z.string().optional(),
    sort: z.enum(['placed_desc', 'placed_asc', 'number_desc']).default('placed_desc'),
    /** Demo orders are left out unless asked for, or the list is for one shop. */
    demo: z.enum(['hide', 'include', 'only']).default('hide'),
});
export const updateOrderRequest = z.object({
    internalNote: z.string().max(4000).nullable().optional(),
    billingAddress: addressSnapshot.optional(),
    shippingAddress: addressSnapshot.nullable().optional(),
});
export const orderActionRequest = z.object({
    action: z.enum(['hold', 'release_hold', 'resolve', 'cancel']),
    note: z.string().max(2000).optional(),
});
// --- Shops ------------------------------------------------------------------
export const shopStatus = z.enum(['not_started', 'ongoing', 'all_packed', 'all_shipped', 'archived']);
/** 3x's shop types as seen in production: `gift`, `login`, `standard_v2` (older: `regular`, `standard`). Kept as a string so new ones pass through. */
export const shopType = z.string();
/** 3x's business areas (Shop::BUSINESS_AREA_*). */
export const BUSINESS_AREAS = { 1: 'B2B merchandise', 2: 'B2B gaver', 3: 'B2B jubilæum', 4: 'B2B pointshop', 5: 'B2B lagersalg', 6: 'B2B benefit shop', 7: 'B2B promotion' };
/**
 * How the warehouse works a shop's orders: `continuous` = one order at a time as they come
 * (merchandise, webshops, point shops, Gaveuniverset with continuous delivery); `batch` = after
 * the shop closes, in picking batches (christmas and other gift shops).
 */
export const shopFlow = z.enum(['continuous', 'batch']);
export const shopDto = z.object({
    id: uuid,
    externalId: z.number().int(),
    name: z.string(),
    giftuniverse: z.boolean(),
    deliveryDate: z.string().nullable(),
    deliveryWeek: z.string().nullable(),
    priority: z.number().int(),
    shipmentDate: z.string().nullable(),
    status: shopStatus,
    packingInstructions: z.string().nullable(),
    /** Mirrored from 3x's shop API; null until the shop has been synced. */
    type: shopType.nullable(),
    businessArea: z.number().int().nullable(),
    businessAreaLabel: z.string().nullable(),
    opensAt: z.string().nullable(),
    closesAt: z.string().nullable(),
    continuousDelivery: z.boolean(),
    x3Archived: z.boolean(),
    x3Active: z.boolean(),
    customerNumber: z.string().nullable(),
    /** Set here (Økonomi customer); invoices use it before the 3x customerNumber. */
    financeCustomerNumber: z.string().nullable(),
    currency: z.string().nullable(),
    /** The rule's result, or the override. */
    flow: shopFlow,
    flowOverride: shopFlow.nullable(),
    /** Continuous shops: release payable orders to picking automatically. */
    autoRelease: z.boolean(),
    syncedAt: isoDateTime.nullable(),
    packFromWarehouseId: uuid.nullable(),
    orderCount: z.number().int(),
    openOrderCount: z.number().int(),
    firstOrderAt: isoDateTime.nullable(),
    lastOrderAt: isoDateTime.nullable(),
    archivedAt: isoDateTime.nullable(),
    demo: z.boolean(),
    demoSourceShopId: uuid.nullable(),
    slug: z.string().nullable(),
});
export const shopWorkRowDto = z.object({
    sku: z.string(),
    title: z.string(),
    variantId: uuid.nullable(),
    ordered: z.number().int(),
    shipped: z.number().int(),
    open: z.number().int(),
});
export const shopWorkViewDto = z.object({
    shop: shopDto,
    byStatus: z.record(z.string(), z.number().int()),
    rows: z.array(shopWorkRowDto.extend({ available: z.number().int(), packable: z.number().int() })),
    /** Orders in `new` whose every line is available now, and the rest. */
    packableOrders: z.number().int(),
    unpackableOrders: z.number().int(),
});
/** The raw 3x shop record (ShopResource), for the settings panel. */
export const shopSettingsDto = z.record(z.string(), z.unknown());
export const updateShopRequest = z.object({
    packFromWarehouseId: uuid.nullable().optional(),
    flowOverride: shopFlow.nullable().optional(),
    autoRelease: z.boolean().optional(),
    priority: z.number().int().min(0).max(100).optional(),
    shipmentDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    packingInstructions: z.string().max(2000).nullable().optional(),
    status: shopStatus.optional(),
    financeCustomerNumber: z.string().trim().min(1).max(40).nullable().optional(),
});
/** Whether a shop can be deleted: `blockers` says why not (then archive it), `removes` what goes with it. */
export const shopDeleteCheckDto = z.object({
    deletable: z.boolean(),
    blockers: z.array(z.string()),
    removes: z.object({ customers: z.number().int(), products: z.number().int(), pages: z.number().int(), domains: z.array(z.string()) }),
});
// --- 3x feed -----------------------------------------------------------------
export const feedRunDto = z.object({
    id: uuid,
    kind: z.string(),
    params: z.unknown(),
    startedAt: isoDateTime,
    finishedAt: isoDateTime.nullable(),
    ok: z.boolean().nullable(),
    fetched: z.number().int(),
    created: z.number().int(),
    updated: z.number().int(),
    skipped: z.number().int(),
    errors: z.number().int(),
    message: z.string().nullable(),
});
export const feedStatusDto = z.object({
    enabled: z.boolean(),
    testMode: z.boolean(),
    baseUrl: z.string(),
    tokenSet: z.boolean(),
    lastRun: feedRunDto.nullable(),
    lastSuccessAt: isoDateTime.nullable(),
    orderCount: z.number().int(),
    shopCount: z.number().int(),
    variantCount: z.number().int(),
    backfill: z.object({
        enabled: z.boolean(),
        done: z.boolean(),
        start: z.number().int().nullable(),
        cursor: z.number().int().nullable(),
        end: z.number().int().nullable(),
    }),
});
export const importShopRequest = z.object({ externalShopId: z.number().int().positive() });
//# sourceMappingURL=orders.js.map